import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, DollarSign, Loader2, Receipt, Trash2 } from "lucide-react";

const REASON_LABELS = {
  fuel: "Fuel",
  supplies: "Supplies (SH / chemicals)",
  tools: "Tools",
  other: "Other",
};

const STATUS_STYLES = {
  pending: "bg-amber-50 text-amber-700 border border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  rejected: "bg-rose-50 text-rose-700 border border-rose-200",
};

/**
 * Lead technician reimbursement request page.
 * Only reachable for users with `is_lead_technician = true`. The route
 * guard lives in App.js.
 */
export default function Reimbursements() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    store_name: "",
    amount: "",
    reason: "fuel",
    description: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reimbursements/mine");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.store_name.trim()) return toast.error("Store name is required.");
    const amt = parseFloat(form.amount);
    if (!(amt > 0)) return toast.error("Enter a valid amount over $0.");
    setSaving(true);
    try {
      await api.post("/reimbursements", {
        store_name: form.store_name.trim(),
        amount: amt,
        reason: form.reason,
        description: form.description.trim(),
      });
      toast.success("Request submitted — send your receipt photo in the office chat now.");
      setForm({ store_name: "", amount: "", reason: "fuel", description: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const onCancel = async (id) => {
    if (!confirm("Withdraw this request? You can re-submit it later if needed.")) return;
    try {
      await api.delete(`/reimbursements/${id}`);
      toast.success("Withdrew request.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <div className="mx-auto max-w-2xl px-4 py-6">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4"
          data-testid="reimb-back-link"
        >
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </Link>

        <h1 className="text-3xl font-extrabold font-display tracking-tighter text-slate-900">
          Reimbursements
        </h1>
        <p className="text-slate-600 mt-1 mb-5">
          Log an out-of-pocket purchase for reimbursement. Approved requests are added to your next
          paycheck as an after-tax line item — you get the full amount back.
        </p>

        <Card className="border-slate-200 shadow-sm mb-6">
          <CardContent className="p-5">
            <form onSubmit={onSubmit} className="space-y-4" data-testid="reimb-form">
              <div>
                <Label htmlFor="reimb-store">Store name</Label>
                <Input
                  id="reimb-store"
                  value={form.store_name}
                  onChange={(e) => setForm({ ...form, store_name: e.target.value })}
                  placeholder="e.g. Home Depot, Shell"
                  data-testid="reimb-store-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="reimb-amount">Amount</Label>
                  <div className="relative">
                    <DollarSign className="h-4 w-4 absolute left-2 top-3 text-slate-400" />
                    <Input
                      id="reimb-amount"
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.amount}
                      onChange={(e) => setForm({ ...form, amount: e.target.value })}
                      placeholder="0.00"
                      className="pl-7"
                      data-testid="reimb-amount-input"
                    />
                  </div>
                </div>
                <div>
                  <Label>Reason</Label>
                  <Select
                    value={form.reason}
                    onValueChange={(v) => setForm({ ...form, reason: v })}
                  >
                    <SelectTrigger data-testid="reimb-reason-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(REASON_LABELS).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="reimb-desc">Notes (optional)</Label>
                <Textarea
                  id="reimb-desc"
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="What was it for? Which job? Any details help the admin approve faster."
                  data-testid="reimb-desc-input"
                />
              </div>

              <div className="rounded-xl bg-sky-50 border border-sky-200 p-3 text-sm text-sky-900" data-testid="reimb-receipt-instruction">
                <b>Important:</b> after you submit, take a photo of the receipt and send it in the office
                chat right away. The admin needs to see the receipt before approving.
              </div>

              <Button
                type="submit"
                disabled={saving}
                className="w-full h-12 bg-sky-600 hover:bg-sky-700"
                data-testid="reimb-submit-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Receipt className="h-4 w-4 mr-2" />}
                Submit request
              </Button>
            </form>
          </CardContent>
        </Card>

        <h2 className="text-lg font-bold text-slate-800 mb-2">Your requests</h2>
        {loading && <p className="text-slate-500 text-sm">Loading…</p>}
        {!loading && items.length === 0 && (
          <div className="rounded-xl bg-white border border-dashed border-slate-300 p-6 text-center text-slate-500 text-sm">
            No requests yet. Submit one above and it'll show up here.
          </div>
        )}
        <div className="space-y-2">
          {items.map((r) => (
            <Card key={r.id} className="border-slate-200" data-testid={`reimb-row-${r.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold ${STATUS_STYLES[r.status] || ""}`}>
                        {r.status}
                      </span>
                      <span className="text-xs text-slate-500">{new Date(r.submitted_at).toLocaleDateString()}</span>
                    </div>
                    <div className="font-bold text-slate-900 truncate">
                      {r.store_name} — ${r.amount.toFixed(2)}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {REASON_LABELS[r.reason] || r.reason}
                      {r.description ? ` — ${r.description}` : ""}
                    </div>
                    {r.admin_notes && (
                      <div className="text-xs mt-2 rounded bg-slate-50 border border-slate-200 p-2 text-slate-700">
                        <b>Admin note:</b> {r.admin_notes}
                      </div>
                    )}
                    {r.paid_on_paystub_id && (
                      <div className="text-xs mt-2 text-emerald-700 font-semibold">
                        Paid on your paystub · {r.paid_at ? new Date(r.paid_at).toLocaleDateString() : ""}
                      </div>
                    )}
                  </div>
                  {r.status === "pending" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onCancel(r.id)}
                      className="text-rose-600 hover:text-rose-700"
                      data-testid={`reimb-cancel-${r.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
